import toast, { superAdminToast } from './toast';
import { getDownloadLink, getSourceLink } from '../api/vault/assetDetails';
import { createAssetStatus } from '../api/vault/assetStatus/api';
import { ActionStatus, ActionType } from '../api/vault/assetStatus/interfaces';
import { Sport } from '../enums';
import { VAULT_VIDEO_TYPE } from '../constants';
import { isLacrosse } from './exchange';
import store from '../state/store';
import { IAppState } from '../state/reducers';
import { selectExchangeFileDownloadDisabled } from '../state/user/userOrgRole';

const getFileNameType = (uri: string): { source: string, fileName: string; fileType: string; } => {
  const source = decodeURIComponent(uri);
  /* eslint-disable no-useless-escape */
  const result = /http?s:\/\/.*(filename=[a-zA-Z0-9\.\s@-]*.*\.[a-zA-Z0-9]{0,6}).*/g.exec(source);
  const param = result.find((s) => s.startsWith('filename'));

  const fileName = param.split('=')[1];
  const fileType = fileName.split('.')[1];

  return {
    source,
    fileName,
    fileType,
  };
};

export const downloadAsset = async (rawAssetDetails: any, isVaultSuperAdmin: boolean, isPreview = false) => {
  const {
    _id,
    title,
    attachments,
    videoType,
    exchangeSport,
  } = rawAssetDetails;
  let downloadLink: string;
  let reason = '';
  try {
    const response = await getDownloadLink({ id: _id }, 1, 'download', isPreview);
    downloadLink = response.data.data;
  } catch (e) {
    if (isPreview) {
      reason = 'Asset processing preview, not ready yet.  Please try again later.';
    } else {
      reason = 'Asset processing transcode, not ready yet.  Please try again later.';
    }
    console.error(`[QA] Error retrieving preview download link for asset "${_id}"`);
  }

  if (downloadLink) {
    const { fileName } = getFileNameType(downloadLink);

    if (!window.open(downloadLink, '_blank')) {
      superAdminToast(false, 'Please disable pop up blocker to complete this action', { type: toast.TYPE.ERROR });
    } else {
      const hide = videoType === VAULT_VIDEO_TYPE.Exchange;
      // Creating asset status to keep track of client downloads in the asset status statistics
      await createAssetStatus({
        assetName: title,
        assetId: _id,
        actionType: ActionType.Download,
        actionStatus: ActionStatus.Completed,
        actionProgress: '100',
        videoType,
        deleted: hide, // This is to hide the asset status from jobs screen if downloading from exchange
        // orgId will be assigned on creation in api based off logged in user
      });

      if (attachments && attachments.length > 0 && !(videoType === VAULT_VIDEO_TYPE.Exchange && exchangeSport && exchangeSport === Sport.Softball)) {
        // Content preference: org only wants the asset, not the exchange file.
        // Cast needed because the untyped JS reducers make the store's inferred state
        // structurally incompatible with IAppState.
        const exchangeFileDownloadDisabled = selectExchangeFileDownloadDisabled(store.getState() as unknown as IAppState);
        attachments.forEach((item: any) => {
          if (isLacrosse(exchangeSport) && item.attachmentType === 'xchange') return; // Skip downloading xchange attachments for lacrosse
          if (exchangeFileDownloadDisabled && item.attachmentType === 'xchange') {
            console.log(`[QA] Skipping xchange attachment for asset "${_id}" - exchange file download is disabled for this org`);
            return;
          }
          const link = document.createElement('a');
          const text = encodeURIComponent(item.content);
          const type = item.attachmentType;
          link.setAttribute('href', `data:text/plain;charset=utf-8,${text}`);
          let downloadFileName = `${fileName.slice(0, fileName.lastIndexOf('.'))}.${type}`;
          if (item.attachmentType !== 'xchange') downloadFileName = item.sourceFileName;
          link.setAttribute('download', downloadFileName);
          link.target = '_blank';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
      }
    }
  } else {
    if (reason === '') {
      reason = 'Asset processing transcode, not ready yet.  Please try again later.';
    }
    superAdminToast(false, `"${title}" can\'t be downloaded: ${reason}`, { type: toast.TYPE.ERROR });
  }
};

export const downloadSource = async (rawAssetDetails: any, isVaultSuperAdmin: boolean) => {
  const {
    _id,
    title,
    attachments,
    videoType,
  } = rawAssetDetails;
  let downloadLink: string;
  try {
    const response = await getSourceLink({ id: _id }, 1, 'download');
    downloadLink = response.data.data;
  } catch (e) {
    console.error(`[QA] Error retrieving source download link for asset "${_id}"`);
  }
  if (downloadLink) {
    if (!window.open(downloadLink, '_blank')) {
      superAdminToast(isVaultSuperAdmin, 'Please disable pop up blocker to complete this action', { type: toast.TYPE.ERROR });
    } else {
      // Creating asset status to keep track of client downloads in the asset status statistics
      const hidden = videoType === VAULT_VIDEO_TYPE.Exchange;
      await createAssetStatus({
        assetName: title,
        assetId: _id,
        actionType: ActionType.Download,
        actionStatus: ActionStatus.Completed,
        actionProgress: '100',
        videoType,
        deleted: hidden, // This is to hide the asset status from jobs screen if downloading from exchange
        // orgId will be assigned on creation in api based off logged in user
      });
    }
  } else {
    superAdminToast(isVaultSuperAdmin, `"${title}" can\'t be downloaded`, { type: toast.TYPE.ERROR });
  }
};
